/**
 * useReportEditor — Centralised state management for the quarterly report editor.
 *
 * Replaces the ~10 ad-hoc setFormData callbacks in EditableContentPanel with a
 * single useReducer + dispatch pattern. Includes an undo/redo history stack.
 */
import { useReducer, useCallback, useEffect, useRef } from 'react';
import { DEFAULT_LAYOUT } from './BlockRenderer';
import { migratePageBlocks, getDefaultBlockSize } from './layoutUtils';

// All commentary section keys that need initialising
const ALL_CONTENT_KEYS = [
  'intro_text', 'market_overview', 'market_overview_callouts',
  'performance_review', 'performance_review_callouts',
  'corporate_debt_review', 'government_bonds_review',
  'international_equities_review', 'australian_equities_review',
  'real_assets_review', 'global_alpha_review', 'outlook',
];

// ── Action types ──
const SET_FIELD = 'SET_FIELD';
const SET_LAYOUT = 'SET_LAYOUT';
const ADD_BLOCK = 'ADD_BLOCK';
const REMOVE_BLOCK = 'REMOVE_BLOCK';
const ADD_PAGE = 'ADD_PAGE';
const REMOVE_PAGE = 'REMOVE_PAGE';
const SET_CUSTOM_CONTENT = 'SET_CUSTOM_CONTENT';
const SET_HEADING = 'SET_HEADING';
const SET_HEADING_STYLE = 'SET_HEADING_STYLE';
const SET_BLOCK_DEF = 'SET_BLOCK_DEF';
const SET_PAGE_BLOCKS = 'SET_PAGE_BLOCKS';
const RESET_ALL = 'RESET_ALL';
const INIT = 'INIT';
const UNDO = 'UNDO';
const REDO = 'REDO';

// ── Reducer ──
function reportReducer(state, action) {
  switch (action.type) {
    case INIT:
      return {
        ...state,
        formData: action.payload,
        undoStack: [],
        redoStack: [],
      };

    case SET_FIELD: {
      const prev = state.formData;
      return {
        ...state,
        formData: { ...prev, [action.key]: action.value },
        undoStack: [...state.undoStack, prev].slice(-30),
        redoStack: [],
      };
    }

    case SET_LAYOUT: {
      const prev = state.formData;
      return {
        ...state,
        formData: { ...prev, page_layout: action.layout },
        undoStack: [...state.undoStack, prev].slice(-30),
        redoStack: [],
      };
    }

    case ADD_BLOCK: {
      const prev = state.formData;
      const layout = (prev.page_layout || DEFAULT_LAYOUT).map(p => ({
        ...p,
        blocks: [...p.blocks],
      }));

      if (layout[action.pageIndex]) {
        const existingBlocks = layout[action.pageIndex].blocks;
        const maxY = existingBlocks.reduce((max, b) => Math.max(max, (b.y || 0) + (b.h || 6)), 0);
        const size = getDefaultBlockSize(action.blockId);
        layout[action.pageIndex].blocks.push({
          id: action.blockId,
          x: 0,
          y: maxY,
          w: size.w,
          h: size.h,
        });
      }

      const updates = { page_layout: layout };
      if (action.blockDef) {
        updates.custom_blocks = { ...prev.custom_blocks, [action.blockId]: action.blockDef };
        if (action.blockDef.type === 'text') {
          updates.custom_content = { ...prev.custom_content, [action.blockId]: '' };
        }
        if (action.blockDef.type === 'image') {
          updates.custom_content = { ...prev.custom_content, [action.blockId]: action.imageData || '' };
        }
      }

      return {
        ...state,
        formData: { ...prev, ...updates },
        undoStack: [...state.undoStack, prev].slice(-30),
        redoStack: [],
      };
    }

    case REMOVE_BLOCK: {
      const prev = state.formData;
      const layout = (prev.page_layout || DEFAULT_LAYOUT).map(p => ({
        ...p,
        blocks: p.blocks.filter(b => (typeof b === 'string' ? b : b.id) !== action.blockId),
      }));

      const updates = { page_layout: layout };
      if (prev.custom_blocks && prev.custom_blocks[action.blockId]) {
        const newCustomBlocks = { ...prev.custom_blocks };
        delete newCustomBlocks[action.blockId];
        updates.custom_blocks = newCustomBlocks;

        if (prev.custom_content && prev.custom_content[action.blockId] !== undefined) {
          const newCustomContent = { ...prev.custom_content };
          delete newCustomContent[action.blockId];
          updates.custom_content = newCustomContent;
        }
      }

      return {
        ...state,
        formData: { ...prev, ...updates },
        undoStack: [...state.undoStack, prev].slice(-30),
        redoStack: [],
      };
    }

    case ADD_PAGE: {
      const prev = state.formData;
      const layout = (prev.page_layout || DEFAULT_LAYOUT).map(p => ({ ...p, blocks: [...p.blocks] }));
      const nextPage = layout.length + 1;
      layout.push({ page: nextPage, blocks: [] });
      return {
        ...state,
        formData: { ...prev, page_layout: layout, [`page_${nextPage}_callouts`]: [] },
        undoStack: [...state.undoStack, prev].slice(-30),
        redoStack: [],
      };
    }

    case REMOVE_PAGE: {
      const prev = state.formData;
      const layout = (prev.page_layout || DEFAULT_LAYOUT).map(p => ({ ...p, blocks: [...p.blocks] }));
      const page = layout[action.pageIndex];
      if (!page) return state;

      const updates = {};
      let newCustomBlocks = { ...prev.custom_blocks };
      let newCustomContent = { ...prev.custom_content };
      let customChanged = false;

      page.blocks.forEach(b => {
        const blockId = typeof b === 'string' ? b : b.id;
        if (newCustomBlocks[blockId]) {
          delete newCustomBlocks[blockId];
          customChanged = true;
        }
        if (newCustomContent[blockId] !== undefined) {
          delete newCustomContent[blockId];
          customChanged = true;
        }
      });

      // Collect existing page callouts, remove the deleted page, renumber
      const oldCallouts = layout.map((_, i) => prev[`page_${i + 1}_callouts`] || []);
      oldCallouts.splice(action.pageIndex, 1);
      layout.forEach((_, i) => { updates[`page_${i + 1}_callouts`] = undefined; });
      oldCallouts.forEach((c, i) => { updates[`page_${i + 1}_callouts`] = c; });

      layout.splice(action.pageIndex, 1);
      layout.forEach((p, i) => { p.page = i + 1; });

      updates.page_layout = layout;
      if (customChanged) {
        updates.custom_blocks = newCustomBlocks;
        updates.custom_content = newCustomContent;
      }

      return {
        ...state,
        formData: { ...prev, ...updates },
        undoStack: [...state.undoStack, prev].slice(-30),
        redoStack: [],
      };
    }

    case SET_CUSTOM_CONTENT: {
      const prev = state.formData;
      return {
        ...state,
        formData: {
          ...prev,
          custom_content: { ...prev.custom_content, [action.blockId]: action.html },
        },
        // Don't push to undo for every keystroke — text edits are high-frequency
        undoStack: state.undoStack,
        redoStack: state.redoStack,
      };
    }

    case SET_HEADING: {
      const prev = state.formData;
      return {
        ...state,
        formData: {
          ...prev,
          heading_overrides: { ...prev.heading_overrides, [action.blockId]: action.text },
        },
        // Don't push to undo for every keystroke
        undoStack: state.undoStack,
        redoStack: state.redoStack,
      };
    }

    case SET_HEADING_STYLE: {
      const prev = state.formData;
      const existing = prev.heading_styles?.[action.blockId] || {};
      return {
        ...state,
        formData: {
          ...prev,
          heading_styles: {
            ...prev.heading_styles,
            [action.blockId]: { ...existing, ...action.styles },
          },
        },
        undoStack: [...state.undoStack, prev].slice(-30),
        redoStack: [],
      };
    }

    case SET_BLOCK_DEF: {
      const prev = state.formData;
      return {
        ...state,
        formData: {
          ...prev,
          custom_blocks: {
            ...prev.custom_blocks,
            [action.blockId]: action.blockDef,
          },
        },
        // No undo for high-frequency colour picks
        undoStack: state.undoStack,
        redoStack: state.redoStack,
      };
    }

    case SET_PAGE_BLOCKS: {
      const prev = state.formData;
      const layout = (prev.page_layout || DEFAULT_LAYOUT).map(p => ({ ...p, blocks: [...p.blocks] }));
      if (layout[action.pageIndex]) {
        layout[action.pageIndex].blocks = action.blocks;
      }
      return {
        ...state,
        formData: { ...prev, page_layout: layout },
        undoStack: [...state.undoStack, prev].slice(-30),
        redoStack: [],
      };
    }

    case RESET_ALL: {
      const prev = state.formData;
      return {
        ...state,
        formData: action.payload,
        undoStack: [...state.undoStack, prev].slice(-30),
        redoStack: [],
      };
    }

    case UNDO: {
      if (state.undoStack.length === 0) return state;
      const prev = state.undoStack[state.undoStack.length - 1];
      return {
        ...state,
        formData: prev,
        undoStack: state.undoStack.slice(0, -1),
        redoStack: [...state.redoStack, state.formData].slice(-30),
      };
    }

    case REDO: {
      if (state.redoStack.length === 0) return state;
      const next = state.redoStack[state.redoStack.length - 1];
      return {
        ...state,
        formData: next,
        undoStack: [...state.undoStack, state.formData].slice(-30),
        redoStack: state.redoStack.slice(0, -1),
      };
    }

    default:
      return state;
  }
}

// ── Initialiser: build formData from raw content prop ──
function buildInitialFormData(content) {
  const data = {};
  for (const key of ALL_CONTENT_KEYS) {
    data[key] = content?.[key] || '';
  }
  data.disclaimer = content?.disclaimer || '';
  data.performance_footnote = content?.performance_footnote || '';

  const rawLayout = content?.page_layout || null;
  if (rawLayout) {
    data.page_layout = rawLayout.map(page => ({
      ...page,
      blocks: migratePageBlocks(page.blocks),
    }));
  } else {
    data.page_layout = null;
  }
  data.custom_blocks = content?.custom_blocks || {};
  data.custom_content = content?.custom_content || {};
  data.heading_overrides = content?.heading_overrides || {};
  data.heading_styles = content?.heading_styles || {};
  data.title_box = content?.title_box || {};
  data.colour_overrides = content?.colour_overrides || {};

  // Load page-level callouts
  const layout = data.page_layout || DEFAULT_LAYOUT;
  layout.forEach((_, i) => {
    const key = `page_${i + 1}_callouts`;
    data[key] = content?.[key] || [];
  });

  return data;
}

// ── Hook ──
export default function useReportEditor(content, onChange) {
  const [state, dispatch] = useReducer(reportReducer, {
    formData: {},
    undoStack: [],
    redoStack: [],
  });

  // Initialise from content prop
  const contentRef = useRef(content);
  useEffect(() => {
    // Only re-init when content identity changes (new load from server)
    if (content !== contentRef.current) {
      contentRef.current = content;
      dispatch({ type: INIT, payload: buildInitialFormData(content) });
    }
  }, [content]);

  // Also init on first mount
  useEffect(() => {
    dispatch({ type: INIT, payload: buildInitialFormData(content) });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Notify parent of live edits
  useEffect(() => {
    if (onChange) onChange(state.formData);
  }, [state.formData, onChange]);

  // ── Dispatch helpers (stable references) ──
  const setField = useCallback((key, value) => {
    dispatch({ type: SET_FIELD, key, value });
  }, []);

  const setLayout = useCallback((layout) => {
    dispatch({ type: SET_LAYOUT, layout });
  }, []);

  const addBlock = useCallback((pageIndex, blockId, blockDef, imageData) => {
    dispatch({ type: ADD_BLOCK, pageIndex, blockId, blockDef, imageData });
  }, []);

  const removeBlock = useCallback((blockId) => {
    dispatch({ type: REMOVE_BLOCK, blockId });
  }, []);

  const addPage = useCallback(() => {
    dispatch({ type: ADD_PAGE });
  }, []);

  const removePage = useCallback((pageIndex) => {
    dispatch({ type: REMOVE_PAGE, pageIndex });
  }, []);

  const setCustomContent = useCallback((blockId, html) => {
    dispatch({ type: SET_CUSTOM_CONTENT, blockId, html });
  }, []);

  const setHeading = useCallback((blockId, text) => {
    dispatch({ type: SET_HEADING, blockId, text });
  }, []);

  const setHeadingStyle = useCallback((blockId, styles) => {
    dispatch({ type: SET_HEADING_STYLE, blockId, styles });
  }, []);

  const setBlockDef = useCallback((blockId, blockDef) => {
    dispatch({ type: SET_BLOCK_DEF, blockId, blockDef });
  }, []);

  const setPageBlocks = useCallback((pageIndex, blocks) => {
    dispatch({ type: SET_PAGE_BLOCKS, pageIndex, blocks });
  }, []);

  const resetAll = useCallback(() => {
    const blankData = {};
    for (const key of ALL_CONTENT_KEYS) {
      // Callouts keys must be arrays to satisfy backend Pydantic validation
      blankData[key] = key.endsWith('_callouts') ? [] : '';
    }
    blankData.disclaimer = '';
    blankData.performance_footnote = '';
    blankData.page_layout = null;
    blankData.custom_blocks = {};
    blankData.custom_content = {};
    blankData.heading_overrides = {};
    blankData.heading_styles = {};
    blankData.title_box = {};
    blankData.colour_overrides = {};
    dispatch({ type: RESET_ALL, payload: blankData });
    return blankData;
  }, []);

  const undo = useCallback(() => dispatch({ type: UNDO }), []);
  const redo = useCallback(() => dispatch({ type: REDO }), []);

  return {
    formData: state.formData,
    canUndo: state.undoStack.length > 0,
    canRedo: state.redoStack.length > 0,
    setField,
    setLayout,
    addBlock,
    removeBlock,
    addPage,
    removePage,
    setCustomContent,
    setHeading,
    setHeadingStyle,
    setBlockDef,
    setPageBlocks,
    resetAll,
    undo,
    redo,
  };
}
